import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tenantsApi, type TenantFilters, type TenantUsersFilters } from '../services/tenantsApi';
import type { Tenant, TenantCreateInput } from '../types/platform.types';
import { toast } from 'sonner';

export const useTenants = (filters?: TenantFilters) => {
  return useQuery({
    queryKey: ['platform', 'tenants', filters],
    queryFn: () => tenantsApi.getAll(filters),
  });
};

export const useTenant = (id: string) => {
  return useQuery({
    queryKey: ['platform', 'tenants', id],
    queryFn: () => tenantsApi.getById(id),
    enabled: !!id,
  });
};

export const useCreateTenant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tenant: TenantCreateInput) => tenantsApi.create(tenant),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
      toast.success('Tenant created successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create tenant');
    },
  });
};

export const useUpdateTenant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Tenant> }) => tenantsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
      toast.success('Tenant updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update tenant');
    },
  });
};

export const useDeleteTenant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tenantsApi.delete(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
      toast.success(data || 'Tenant deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete tenant');
    },
  });
};

export const useActivateTenant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tenantsApi.activate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
      toast.success('Tenant activated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to activate tenant');
    },
  });
};

export const useSuspendTenant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => tenantsApi.suspend(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
      toast.success('Tenant suspended successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to suspend tenant');
    },
  });
};

export const useTenantUsers = (id: string, filters?: TenantUsersFilters) => {
  return useQuery({
    queryKey: ['platform', 'tenants', id, 'users', filters],
    queryFn: () => tenantsApi.getTenantUsers(id, filters),
    enabled: !!id,
  });
};
